import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../services/api';
import {
  Spinner, ArrowLeft
} from '@phosphor-icons/react';

const PreparePage = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const { data } = await axios.get(`${API}/prepare/summary`);
        setSummary(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <header className="glass-header sticky top-0 z-10 px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate('/home')} className="text-slate-600 hover:text-slate-900">
          <ArrowLeft size={24} />
        </button>
        <h1 className="font-medium text-slate-900">Prepare for Visit</h1>
      </header>

      <div className="flex-1 px-6 py-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner size={32} className="animate-spin text-[#235347]" />
          </div>
        ) : summary ? (
          <div className="space-y-6">
            <div className="card-base p-5 gradient-bg text-white">
              <h2 className="text-lg font-medium mb-2">Health Summary for Doctor</h2>
              <p className="text-white/80 text-sm">Generated {new Date(summary.generated_at).toLocaleDateString()}</p>
            </div>

            {summary.conditions?.length > 0 && (
              <div className="card-base p-5">
                <h2 className="font-medium text-slate-900 mb-3">Active Conditions</h2>
                <ul className="space-y-2">
                  {summary.conditions.map((c, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-600">
                      <div className="w-2 h-2 rounded-full bg-[#235347]" />
                      {c.name} {c.severity && `(${c.severity})`}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {summary.current_medications?.length > 0 && (
              <div className="card-base p-5">
                <h2 className="font-medium text-slate-900 mb-3">Current Medications</h2>
                <ul className="space-y-2">
                  {summary.current_medications.map((m, i) => (
                    <li key={i} className="text-slate-600">
                      <span className="font-medium">{m.name}</span>
                      {m.dosage && ` - ${m.dosage}`}
                      {m.frequency && ` (${m.frequency})`}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {summary.recent_trends && (
              <div className="card-base p-5">
                <h2 className="font-medium text-slate-900 mb-3">Recent Trends ({summary.recent_trends.days_tracked} days tracked)</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Avg Mood</p>
                    <p className="text-2xl font-light text-[#235347]">{summary.recent_trends.average_mood || '-'}/10</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Avg Sleep</p>
                    <p className="text-2xl font-light text-[#235347]">{summary.recent_trends.average_sleep || '-'}h</p>
                  </div>
                </div>
              </div>
            )}

            {summary.reported_symptoms?.length > 0 && (
              <div className="card-base p-5">
                <h2 className="font-medium text-slate-900 mb-3">Reported Symptoms</h2>
                <div className="flex flex-wrap gap-2">
                  {summary.reported_symptoms.map((s, i) => (
                    <span key={i} className="px-3 py-1 bg-slate-100 rounded-full text-sm text-slate-600">
                      {s.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-slate-500">Unable to generate summary.</p>
          </div>
        )}
      </div>
    </div>
  );
};


export default PreparePage;
